import {
  ArrowDownTrayIcon,
  ArrowsPointingInIcon,
  ChartBarIcon,
  ArrowUturnLeftIcon,
  LinkIcon,
  ScissorsIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  ChatBubbleBottomCenterTextIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  FolderOpenIcon,
  LinkSlashIcon,
  MapIcon,
  MapPinIcon,
  CubeIcon,
  CurrencyDollarIcon,
  BeakerIcon,
  CodeBracketIcon,
  NoSymbolIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  PlusIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  TicketIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import type { ComponentType, ReactNode, SVGProps } from "react";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * The Driver Activity menu's sections, in order: opening a ticket first, deleting it last. Sections a host names
 * that are not listed here (WaterGo's own, such as "Post Holding") go between them and Delete.
 */
export const DRIVER_ACTIVITY_MENU_SECTIONS = [
  "Open",
  "Review",
  "Comments & Attachments",
  "Quantity & Billing",
  "Download",
  "Create",
  "Shift",
  "Split & Merge",
  "Delete",
] as const;

export type DriverActivityMenuSection = (typeof DRIVER_ACTIVITY_MENU_SECTIONS)[number];

const SECTION_ICONS: Record<DriverActivityMenuSection, Icon> = {
  Open: FolderOpenIcon,
  Review: CheckBadgeIcon,
  "Comments & Attachments": ChatBubbleBottomCenterTextIcon,
  "Quantity & Billing": BanknotesIcon,
  Download: ArrowDownTrayIcon,
  Create: PlusIcon,
  Shift: ClockIcon,
  "Split & Merge": LinkSlashIcon,
  Delete: TrashIcon,
};

/**
 * Every built-in Driver Activity action's icon and menu section, in one place. A new action is added here; one
 * that sets its own `icon` or `toolGroup` keeps it, so hosts can still place their own actions.
 */
export const DRIVER_ACTIVITY_ACTION_REGISTRY: Record<string, { icon: Icon; section: DriverActivityMenuSection }> = {
  view: { icon: EyeIcon, section: "Open" },
  edit: { icon: PencilSquareIcon, section: "Open" },
  "edit-selected": { icon: PencilSquareIcon, section: "Open" },
  map: { icon: MapIcon, section: "Open" },
  "view-dispatch": { icon: ClipboardDocumentListIcon, section: "Open" },

  validate: { icon: CheckBadgeIcon, section: "Review" },
  "dispatch-validate": { icon: CheckBadgeIcon, section: "Review" },
  "dispatch-invalidate": { icon: XCircleIcon, section: "Review" },
  compliance: { icon: ShieldCheckIcon, section: "Review" },

  "comment-pickup": { icon: ChatBubbleBottomCenterTextIcon, section: "Comments & Attachments" },
  "comment-delivery": { icon: ChatBubbleLeftEllipsisIcon, section: "Comments & Attachments" },
  attachments: { icon: PaperClipIcon, section: "Comments & Attachments" },

  cost: { icon: BanknotesIcon, section: "Quantity & Billing" },
  "cost-shift": { icon: CurrencyDollarIcon, section: "Quantity & Billing" },
  "tank-level": { icon: ChartBarIcon, section: "Quantity & Billing" },
  "well-allocation": { icon: BeakerIcon, section: "Quantity & Billing" },
  "gauge-audit-pickup": { icon: ScaleIcon, section: "Quantity & Billing" },
  "gauge-audit-delivery": { icon: ScaleIcon, section: "Quantity & Billing" },
  "disable-pickup-demurrage": { icon: NoSymbolIcon, section: "Quantity & Billing" },
  "enable-pickup-demurrage": { icon: CheckCircleIcon, section: "Quantity & Billing" },
  "disable-delivery-demurrage": { icon: NoSymbolIcon, section: "Quantity & Billing" },
  "enable-delivery-demurrage": { icon: CheckCircleIcon, section: "Quantity & Billing" },

  "download-pdf": { icon: DocumentArrowDownIcon, section: "Download" },
  "download-excel": { icon: TableCellsIcon, section: "Download" },

  "new-ticket": { icon: TicketIcon, section: "Create" },
  "new-pickup": { icon: ArrowUpTrayIcon, section: "Create" },
  "new-delivery": { icon: ArrowDownTrayIcon, section: "Create" },
  "add-delivery": { icon: PlusCircleIcon, section: "Create" },
  "add-pickup": { icon: PlusCircleIcon, section: "Create" },
  "new-dispatch": { icon: ClipboardDocumentCheckIcon, section: "Create" },
  "new-shift": { icon: ClockIcon, section: "Create" },

  "edit-shift": { icon: ClockIcon, section: "Shift" },

  "merge-shifts": { icon: ArrowsPointingInIcon, section: "Shift" },
  "split-shift": { icon: ScissorsIcon, section: "Shift" },

  associate: { icon: LinkIcon, section: "Split & Merge" },
  unmatch: { icon: LinkSlashIcon, section: "Split & Merge" },
  "merge-transport-geofence": { icon: MapPinIcon, section: "Split & Merge" },
  "merge-visits": { icon: ArrowsPointingInIcon, section: "Split & Merge" },
  "split-evenly": { icon: ScissorsIcon, section: "Split & Merge" },
  "pickup-splits": { icon: ScissorsIcon, section: "Split & Merge" },
  "delivery-splits": { icon: ScissorsIcon, section: "Split & Merge" },
  "undo-splits": { icon: ArrowUturnLeftIcon, section: "Split & Merge" },

  "remove-coding": { icon: CodeBracketIcon, section: "Quantity & Billing" },
  navigate: { icon: MapIcon, section: "Open" },
  "view-site": { icon: MapPinIcon, section: "Open" },
  "view-operating-area": { icon: MapIcon, section: "Open" },
  "view-route": { icon: MapIcon, section: "Open" },
  "view-well": { icon: BeakerIcon, section: "Open" },
  "view-container": { icon: CubeIcon, section: "Open" },

  delete: { icon: TrashIcon, section: "Delete" },
};

/**
 * What the ticket inspector shows as icon buttons; everything else that applies goes in its More menu. These are
 * the everyday actions on one ticket: look at it, fix it, sign it off, and note or attach something to it.
 */
export const DRIVER_ACTIVITY_INSPECTOR_ACTIONS = [
  "view",
  "edit",
  "validate",
  "dispatch-validate",
  "comment-pickup",
  "comment-delivery",
  "attachments",
];

type Placeable = { id: string; label?: string; group?: string; toolGroup?: string; icon?: ReactNode };

// GWT supplies its own action IDs and groups; match only their presentation to the workspace.
const HOST_ACTION_LABELS: Record<string, string> = {
  "view": "view", "view ticket": "view", "edit": "edit", "map": "map",
  "compliance": "compliance", "validate": "validate", "view dispatch": "view-dispatch",
  "add attachments": "attachments", "add pickup comment": "comment-pickup", "add delivery comment": "comment-delivery",
  "apply shift cost": "cost-shift", "revenue/cost": "cost", "revenue details": "cost", "cost": "cost", "remove coding": "remove-coding",
  "download": "download-pdf", "download pdf": "download-pdf", "download excel": "download-excel",
  "navigate to…": "navigate",
};

/** An action's icon: its own, else the registry's. */
export const activityActionIcon = (action: Placeable): ReactNode => {
  if (action.icon) return action.icon;
  const label = action.label?.trim().toLowerCase() ?? "";
  const navigation = /^view (site|operating area|route|well|container):/.exec(label)?.[1];
  const hostId = navigation ? `view-${navigation.replace(/ /g, "-")}` : HOST_ACTION_LABELS[label];
  const Registered = DRIVER_ACTIVITY_ACTION_REGISTRY[action.id]?.icon
    ?? DRIVER_ACTIVITY_ACTION_REGISTRY[hostId]?.icon;
  return Registered ? <Registered/> : undefined;
};

/** An action's menu section: its own, else the registry's, else by its group. */
export const activityActionSection = (action: Placeable): string =>
  action.toolGroup
  ?? DRIVER_ACTIVITY_ACTION_REGISTRY[action.id]?.section
  ?? (action.group === "create" ? "Create" : action.group === "tools" ? "Other" : "Open");

/** A section's icon in the menu, when it is one of the workspace's own. */
export const activityMenuSectionIcon = (section: string): ReactNode => {
  const SectionIcon = SECTION_ICONS[section as DriverActivityMenuSection];
  return SectionIcon ? <SectionIcon/> : undefined;
};
